import React, { useEffect } from 'react';
import { useStopwatch } from 'react-timer-hook';
import {
  ALL_MODULES,
  EventEmitter,
  getCurrentUserHook,
  getTranslatedText,
  useActivityTracker,
  useIsAuthorisedHook,
  sendMessageToContentJS,
  EventEmitterWithIndex
} from '@nile/components';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import { SVGIcon } from '@superops/super-icons';
import { Tooltip } from 'react-clone-tippy';
import styled from 'styled-components';
import { EditTimer } from '@nile/components/src/Containers/NewWorklog/TimerList';
import { isEmpty, pick } from 'lodash';
import { Button, TextStyle } from '@superops/ui-design-system';
import { useIntl } from 'react-intl';

const TimerCell = styled.div`
  // background: ${(props) => (props.highlight ? '#F7D4F3' : '#F7F7FD')};
  border-radius: 5px;
  padding: 0 10px;
  min-height: 38px;
  transition: background 200ms linear;
`;

const WorklogTimerCellContainer = styled.div`
  .edit-actions {
    visibility: hidden;
    cursor: pointer;
  }
  .manual-tag {
    background: #f6ddc6;
    border-radius: 2px;
    padding: 0px 5px;
    font-size: 12px;
    line-height: 20px;
  }
  .Superops-Button {
    box-shadow: none !important;
  }
`;

const WorklogTimerCell = (props) => {
  const { cell } = props;
  const {
    timerId,
    timespent,
    running,
    notes,
    segments,
    billable,
    workItem,
    technician
  } = cell?.row?.original;
  const intl = useIntl();
  const { editable } = cell.column;
  const isTicket = workItem?.module === ALL_MODULES.ticket;
  const isEditAuthorised = isTicket
    ? useIsAuthorisedHook('tickets.edit')
    : true;
  const isEditable = editable && isEditAuthorised;
  const currentUser = getCurrentUserHook();
  const isLoggedinUser = technician.userId === currentUser?.userId;
  const timer = cell?.row?.original;
  const autoTimer = timer?.type === 'AUTOMATIC';
  const { actions } = cell.column;
  let stopwatchOffset = new Date();
  // if (!autoTimer) {
  //   timer.segments.forEach((segmnt) => {
  //     totalTimespent += segmnt?.timespent * 60 * 60;
  //   });
  // } else {
  // totalTimespent = timespent;
  // }
  stopwatchOffset.setSeconds(stopwatchOffset.getSeconds() + timespent);
  const {
    seconds,
    minutes,
    hours,
    days,
    isRunning,
    start,
    pause,
    reset
  } = useStopwatch({
    autoStart: running,
    offsetTimestamp: stopwatchOffset
  });

  useEffect(() => {
    stopwatchOffset = new Date();
    stopwatchOffset.setSeconds(stopwatchOffset.getSeconds() + timespent);
    reset(stopwatchOffset, running);
  }, [running, timespent]);

  const handleAssociateWorkItem = (module, workItem) => {
    useActivityTracker({
      event:
        module === 'TICKET' ? 'Associated to ticket' : 'Associated to project'
    });
    return actions?.updateWorklogTimerEntry({
      variables: {
        timerEntryInput: {
          timerId: timer?.timerId,
          workItem: pick(workItem, ['workId', 'module'])
        }
      }
    });
  };

  const handleTimerClick = () => {
    if (editable && isLoggedinUser) {
      const inputObj = {
        billable,
        notes: notes,
        type: 'AUTOMATIC',
        workItem:
          workItem?.workId || workItem?.ticketId
            ? {
                workId: workItem?.workId || workItem?.ticketId,
                module: workItem?.module || 'TICKET'
              }
            : null
      };
      if (timerId) {
        actions
          ?.updateWorklogTimerEntry({
            variables: {
              timerEntryInput: {
                ...inputObj,
                timerId,
                running: !running
              }
            }
          })
          .then((res) => {
            const ts = res?.data?.updateWorklogTimerEntry?.timespent;
            const stopwatchOfset = new Date();
            stopwatchOfset.setSeconds(stopwatchOfset.getSeconds() + ts);
            reset(stopwatchOfset, res?.data?.updateWorklogTimerEntry?.running);
            EventEmitterWithIndex.dispatch('refetchGlobalTimers');
            sendMessageToContentJS('refetchGlobalTimers');
          });
      }
    }
  };

  const formatTime = (time) => {
    return String(time).padStart(2, '0');
  };

  return cell?.value || running ? (
    <WorklogTimerCellContainer className="flex center fw">
      <div className={`flex center ${isEditable ? 'editable-cell' : ''}`}>
        <div
          className={`fx actr pl5 ${
            isEditable && isLoggedinUser ? 'pointer' : ''
          }`}
          onClick={autoTimer && workItem?.workId && handleTimerClick}
        >
          {autoTimer ? (
            !workItem?.workId && isEmpty(notes) ? (
              <Tooltip
                title={getTranslatedText(
                  intl,
                  'timer.update.addNote.placeholder'
                )}
                delay={400}
                duration={250}
                position="top-start"
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
                className="cursor-not-allowed"
              >
                <span className="fx actr">
                  <SVGIcon name={running ? 'Pause' : 'Play'} />
                </span>
              </Tooltip>
            ) : (
              <SVGIcon name={running ? 'Pause' : 'Play'} />
            )
          ) : (
            <Tooltip
              title={getTranslatedText(intl, 'workItem.manualTimer')}
              delay={400}
              duration={250}
              position="top-start"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              {/* <Avatar initials="M" size="extraSmall" /> */}
              <div className="manual-tag">M</div>
            </Tooltip>
          )}
          <TimerCell className="ml-10 flex center">
            <TextStyle weight="SemiBold">
              {/* {days ? <span>{formatTime(days)}:</span> : ''} */}
              <span>{formatTime(days ? days * 24 + hours : hours)}</span>:
              <span>{formatTime(minutes)}</span>:
              <span>{formatTime(seconds)}</span>
            </TextStyle>
          </TimerCell>
        </div>

        {isEditable && isLoggedinUser && (
          <div className="edit-actions">
            <EditTimer
              leftInline
              timer={timer}
              autoTimer={autoTimer}
              associateWorkItem
              handleOutsideClick
              handleAssociateWorkItem={handleAssociateWorkItem}
              taskViewOnly
              editWorklogZIndex={515}
            />
          </div>
        )}
      </div>
      {workItem?.workId &&
        isLoggedinUser &&
        isEditAuthorised &&
        !isEmpty(notes) && (
          <span className="mlauto">
            <Tooltip
              title={getTranslatedText(
                intl,
                'workItem.details.worklog.timer.addAsWorklog'
              )}
              delay={400}
              duration={250}
              position="top-start"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <Button
                onClick={() => {
                  cell.column?.actions?.createWorklogEntryFromTimer({
                    variables: {
                      input: [
                        {
                          timerId: timerId
                        }
                      ]
                    }
                  });
                }}
                icon={<SVGIcon name="Worklog" />}
              />
            </Tooltip>
          </span>
        )}
    </WorklogTimerCellContainer>
  ) : (
    <DisabledTag>NA</DisabledTag>
  );
};

export default WorklogTimerCell;
